<template>
    <wrapper-features-templates-operations
    :hideContentWrapper="!showLoader && !applyErrorState && isAccountsEmpty"
    :customBreadcrumbs="customBreadcrumbs"
    :applyErrorState="applyErrorState && !showLoader"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :showStepper="activeStep <= stepperParams.length && !applyErrorState && !showLoader && !isAccountsEmpty"
    :stepperParams="stepperParams"
    :activeStep="activeStep"
    :showFooter="activeStep < 3 && !showLoader && !applyErrorState && !isAccountsEmpty"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :tokenTitleAppend="t('payments.voucher.tokenTitleAppend')"
    :feedbackOperationData="feedbackOperationData"
    :scrollValue="scrollValue"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, payEmisVoucherRequest)"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="navigateBack()">
        <!-- *********************************************
        *												 *
        *                      Content                   *
        *												 *
        ********************************************** -->
        <component
        v-if="!applyErrorState && !showLoader && !isAccountsEmpty"
        :is="getActiveStepComponent"
        :isMobile="getMobileAppState"
        :accountsBalances="getAccountsBalances"
        :vouchers="getEmisVouchers"
        :clearFormErrors="clearFormErrors"
        :favoriteButtonData="favoriteButtonData"
        :formData="formData"
        :mandatoryList="mandatoryList"
        :selectedPayment="selectedPayment"
        :showMandatoryErrors="showMandatoryErrors"
        :responseData="responseData"
        @formDataChange="formDataChangeHandler"
        @formErrorsCleared="clearFormErrors = false"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @scrollTo="scrollValue = $event"
        @showFavoritesModal="showFavoritesModal = true"
        @newPayment="resetPayment"/>
        <!-- Empty-state -->
        <template #empty-state-slot>
            <transition name="common-enter-animation" mode="out-in" appear>
                <empty-state-generic
                v-if="!applyErrorState && !showLoader && isAccountsEmpty"
                illustration="emptyState/payments"
                :title="t('payments.voucher.emptyState.title')"
                :description="t('payments.voucher.emptyState.description')"/>
            </transition>
        </template>
    </wrapper-features-templates-operations>
    <!-- Favorites Modal for Step3 -->
    <favorites-modal
    v-if="showFavoritesModal"
    :modalParams="{ operationId: responseData.data.operationId, type: favoriteButtonData.type }"
    @hideMe="showFavoritesModal = false"
    @requestSuccessful="favoriteModalSuccessHandler($event)"
    @requestError="favoriteModalErrorHandler($event)"/>
    <!-- Feedback Toasts -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    :triggerClose="triggerClose"
    @hideMe="[showFeedbackToast = false, triggerClose = false]"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import FavoritesModal from '@/components/generic/modals/centered/Favorite'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Assets & Helpers */
import { atLeastOneIsEmpty, clearObject } from '@/assets/scripts/objects'
import { createDownloadPaymentReceiptPdf } from '@/network/api/payments/payments.api.js'
import { navigateBack } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { parseSelectedPaymentData } from '@/assets/scripts/parsers/payments'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* props received
@params { Object } - data received from the chosen payment (Payments -> Services -> Payment Selected )
*/
const props = defineProps({
    params: Object
})

/* mapActions & mapGetters */
const {	requestAccountsBalances, requestEmisVouchers, payEmisVoucher, clearFeatureStates } = mapActions(moduleMapper.PAYMENTS_VOUCHER)
const { getAccountsBalances, getEmisVouchers } = mapGetters(moduleMapper.PAYMENTS_VOUCHER)
const { getMobileAppState } = mapGetters()

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)

/* Data */
const applyErrorState = ref(false)
const selectedPayment = ref(props.params)
const isAccountsEmpty = ref(false)

/*************************************************
*                                                *
*               Custom Breadcrumbs               *
*                                                *
*************************************************/
const customBreadcrumbs = computed(() => {
    return {
        currentArea: selectedPayment.value.description,
        parentMenus: [{
            id: 1,
            text: t('payments.services.breadcrumbs.payments')
        }, {
            id: 2,
            text: t('payments.services.breadcrumbs.services'),
            link: routeList.PAYMENTS_SERVICES_PATH
        }]
    }
})

/*************************************************
*                                                *
*                    Steps Data                  *
*                                                *
*************************************************/
const activeStep = ref(0)
const stepperParams = ref([
    { description: t('payments.voucher.stepper.data') },
    { description: t('payments.voucher.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/payments/flows/voucher/Step' + stepNumber))
})

/*************************************************
*                                                *
*                Form Logic Data                 *
*                                                *
*************************************************/
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

const formData = ref({
    originAccount: '',
    currency: '',
    code: '',
    amount: ''
})

const mandatoryList = ref([
    'originAccount',
    'code',
    'amount'
])

const formDataChangeHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const clearInputs = () => {
    clearFormErrors.value = true
    clearObject(formData.value)

    /* Reset Origin Account to Default and Reset Number Searched */
    formData.value.originAccount = getAccountsBalances.value.find(account => { return account.default }).id
    formData.value.currency = getAccountsBalances.value.find(account => { return account.default }).currency
}

const validateInputs = () => {
    ((formData.value.amount && formData.value.amount.length && parseInt(formData.value.amount) === 0) ||
    atLeastOneIsEmpty(formData.value, mandatoryList.value)) ? showMandatoryErrors.value = true : activeStep.value = 2
}

const validatePayment = () => {
    payEmisVoucherRequest()
}

/* Footer Buttons Data */
const footerButtonsParams = ref([
    {
        label: t('payments.voucher.footerButtons.clear'),
        callbackFunction: clearInputs,
        buttonPosition: 'left',
        step: 1
    }, {
        label: t('payments.voucher.footerButtons.forward'),
        callbackFunction: validateInputs,
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'arrow-right-full',
        iconPosition: 'right',
        step: 1
    }, {
        label: t('payments.voucher.footerButtons.back'),
        callbackFunction: () => {
            activeStep.value = 1
            if (showFeedbackToast.value) triggerClose.value = true
        },
        buttonPosition: !getMobileAppState.value ? 'right' : 'left',
        icon: 'arrow-left-full',
        step: 2
    }, {
        label: t('payments.voucher.footerButtons.validate'),
        callbackFunction: validatePayment,
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'check-line',
        iconPosition: 'right',
        step: 2
    }
])

/*************************************************
*                                                *
*                Operation Status                *
*                                                *
*************************************************/
const feedbackOperationData = ref(null)
const responseData = ref()

/*************************************************
*                                                *
*                 Favorite Modal                 *
*                                                *
*************************************************/
const showFavoritesModal = ref(false)
const favoriteButtonData = ref({
    label: t('payments.voucher.step3.buttons.saveAsFavorite'),
    icon: 'star',
    type: 'create',
    favorite: false
})

const favoriteModalSuccessHandler = $event => {
    showFavoritesModal.value = false
    showToastHandler($event)

    if (favoriteButtonData.value.type === 'create') {
        favoriteButtonData.value = {
            label: t('payments.voucher.step3.buttons.removeFavorite'),
            icon: 'star-minus',
            type: 'remove'
        }
    } else {
        favoriteButtonData.value = {
            label: t('payments.voucher.step3.buttons.saveAsFavorite'),
            icon: 'star',
            type: 'create'
        }
    }
}

const favoriteModalErrorHandler = $event => {
    showToastHandler($event, 'error')
    showFavoritesModal.value = false
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const triggerClose = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const showToastHandler = (data, status) => {
    feedbackToastData.value = sucessObjectConstrutor(data.message)
    showFeedbackToast.value = true
}

const successHandler = data => {
    responseData.value = data

    feedbackOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: !data.data.hasReceipt ? null : {
            iconsColor: data.errorLevel === 'INFO' ? 'var(--positive)' : 'var(--warning)',
            emailAction: 'sendPaymentEmailNotification',
            smsAction: 'sendPaymentSMSNotification',
            sendPayload: { id: data.data.paymentId },
            downloadUrl: createDownloadPaymentReceiptPdf(data.data.paymentId),
            operationId: data.data.operationId
        }
    }

    activeStep.value = 3
}

const challengeErrorHandler = () => {
    scrollValue.value = 0
    activeStep.value = 1
}

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn, challengeErrorHandler)
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*               Pay Emis Voucher                 *
*                                                *
*************************************************/
const payEmisVoucherRequest = async () => {
    if (showFeedbackToast.value) triggerClose.value = true
    applyErrorState.value = false
	showOverlayLoader.value = true

    const payload = {
        entityId: props.params.data.emisReference,
        code: formData.value.code,
        data: {
            amount: formData.value.amount,
            operatorId: props.params.id,
            originAccount: formData.value.originAccount
        }
    }

	try {
        await payEmisVoucher(payload)
        showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, payEmisVoucherRequest)
	}
}

/*************************************************
*                                                *
*               Initial Requests                 *
*                                                *
*************************************************/
const initialRequests = async () => {
    applyErrorState.value = false
    isAccountsEmpty.value = false
	showLoader.value = true

	try {
        await requestAccountsBalances()
        await requestEmisVouchers({
            entityId: props.params.data.emisReference
        })

        if (getAccountsBalances.value.length > 0) {
            /* Setting Initial Selected Account and Currency */
            formData.value.originAccount = getAccountsBalances.value.find(account => { return account.default }).id
            formData.value.currency = getAccountsBalances.value.find(account => { return account.default }).currency
        } else {
            isAccountsEmpty.value = true
        }

        /* Setting Form Data if we have some info inside selectedPayment.value
        That's because we previously clicked to reuse our last transfer or favorite data on reuse modal (Payments > Services > Reuse Modal > Selected Row)
        If we have these values, it means that we have to pass this information to put in this form.
        That's why the logic below exists */
        if (selectedPayment.value.amount) {
            const selectedPaymentData = parseSelectedPaymentData(formData.value, selectedPayment.value, getAccountsBalances.value)

            for (const field in formData.value) {
                for (const key in selectedPaymentData) {
                    if (field === key) formDataChangeHandler({ field: field, inputValue: selectedPaymentData[field] })
                }
            }
        }

		showLoader.value = false
        activeStep.value = 1
	} catch (error) {
        applyErrorState.value = true
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, initialRequests)
	}
}

const resetPayment = () => {
    clearInputs()
    clearFeatureStates()
    initialRequests()

    feedbackOperationData.value = null
    favoriteButtonData.value = {
        label: t('payments.voucher.step3.buttons.saveAsFavorite'),
        icon: 'star',
        type: 'create',
        favorite: false
    }
    activeStep.value = 1
}

initialRequests()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
